import React from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const plans = [{
  name: "Breeder Launch",
  setup: "$1,200",
  monthly: "$79/mo.",
  description: "For emerging programs establishing a distinguished digital foundation.",
  features: ["Website Design and Build", "Mobile-First Architecture", "Secure Inquiry System", "About Us and About Our Breed pages", "Quarterly Content Updates"],
  cta: "Book a Consultation",
  recommended: false
}, {
  name: "Breeder Pro",
  setup: "$2,199",
  monthly: "$129/mo.",
  description: "For established kennels seeking high-conversion inquiry flow and brand prestige.",
  features: ["Everything in the Breeder Launch Program", "Custom Puppy Application System", "Sire / Dam Pedigrees", "Priority Concierge Support", "Monthly Content Strategy/Udpates"],
  cta: "Book a Consultation",
  recommended: true
}, {
  name: "Breeder Authority",
  setup: "Custom Pricing",
  monthly: "setup + $?/mo.",
  description: "The ultimate standard in global program management and digital legacy.",
  features: ["Custom needs from Launch and Pro Offerings", "Merchant and Product System", "Content Management System", "24/7 Priority Access", "Weekly Updates"],
  cta: "Book a Consultation",
  recommended: false
}];
export default function Pricing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="6586" id="pricing-overview" ref={ref} className={cn("bg-primary py-24 sm:py-32 transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-secondary font-sans font-bold tracking-[0.2em] uppercase text-xs mb-4">
            The Royal Standard
          </h2>
          <p className="mt-2 text-[4.5rem] font-serif font-medium tracking-tight text-background leading-tight sm:text-7xl">Care for Your Legacy Concierge</p>
          <p className="mt-6 text-[1.1rem] leading-8 text-background/70 font-sans max-w-[600px] mx-auto">Select a tier of distinguished website set-up and monthly service plan tailored to the unique legacy of your breeding program. We offer flexible plans designed to nurture excellence.</p>
        </div>

        <div className="mx-auto grid max-w-lg grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3 items-start">
          {plans.map((plan, i) => <div key={plan.name} data-index={i} className={cn("relative flex flex-col transition-all duration-300", plan.recommended ? "lg:-mt-8 lg:mb-8 z-10" : "")}>
              <Card className={cn("flex flex-col h-full border transition-all duration-300", plan.recommended ? "bg-primary/40 backdrop-blur-md border-secondary shadow-[0_20px_50px_-12px_rgba(212,175,55,0.4)] ring-1 ring-secondary/50 scale-105" : "bg-primary/30 backdrop-blur-sm border-secondary/20 shadow-xl hover:bg-primary/40")}>
                <CardHeader className="pb-8">
                  <div className="flex justify-between items-center gap-4">
                    <CardTitle className="text-2xl font-serif text-background">
                      {plan.name}
                    </CardTitle>
                    {plan.recommended && <Badge variant="default" className="bg-secondary text-primary hover:bg-secondary/90 border-0 rounded-full px-3 font-sans text-[10px] uppercase tracking-wider">
                        Recommended
                      </Badge>}
                  </div>
                  <CardDescription className="mt-4 text-sm leading-6 text-background/60 font-sans min-h-[48px]">
                    {plan.description}
                  </CardDescription>
                  <div className="mt-6 flex items-baseline flex-wrap gap-x-1.5">
                    <span className="text-4xl font-serif text-secondary">
                      {plan.setup}
                    </span>
                    <span className="text-sm font-bold leading-6 text-secondary/60 font-sans">
                      setup + {plan.monthly}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="flex-1">
                  <ul role="list" className="space-y-4 text-sm leading-6 text-background/80 font-sans">
                    {plan.features.map((feature, featureIndex) => <li key={featureIndex} className="flex gap-x-3 items-start" data-index={featureIndex}>
                        <Check className={cn("h-5 w-5 flex-none", plan.recommended ? "text-secondary" : "text-secondary/50")} aria-hidden="true" />
                        <span>{feature}</span>
                      </li>)}
                  </ul>
                </CardContent>
                <CardFooter className="pt-8">
                  <Button asChild className={cn("w-full rounded-full h-12 text-[0.875rem] font-bold uppercase tracking-widest transition-all duration-300", plan.recommended ? "bg-secondary text-primary hover:bg-secondary/90 shadow-md hover:shadow-lg" : "bg-transparent border border-secondary text-secondary hover:bg-secondary/10")}>
                    <Link to="#">
                      {plan.cta}
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}